import * as THREE from 'three';
import { SketchRelationKind } from '../../../../sketch/solver/constraint/relation-compatibility';
import { SingleTargetRelation } from '../../../../sketch/solver/constraint/relationDefinition/SingleTargetRelation';
import type { SketchCircleObject } from '../../../../mesh/sketch/SketchCircleObject';
import { resolvePreferredEdgeLabel } from '../../model/EdgeLabelUtils';
import type { ReferenceDrivenCircleCoincidentConstraintOptions } from '../../../../../types/reference/sketch-entity-reference/ReferenceDrivenCircleCoincidentConstraint';

export type { ReferenceDrivenCircleCoincidentConstraintOptions } from '../../../../../types/reference/sketch-entity-reference/ReferenceDrivenCircleCoincidentConstraint';

/**
 * 参考驱动圆重合关系定义。
 *
 * 该对象只提供固定投影圆的公式载荷，禁止在关系实例中直接改写圆心或半径。
 */
export class ReferenceDrivenCircleCoincidentConstraint extends SingleTargetRelation<SketchCircleObject> {
	/** 引用记录稳定标识。 */
	public readonly referenceId: string;
	/** 关系面板使用的关系类型。 */
	public relationType = SketchRelationKind.OnEdge;

	/** 投影圆的世界坐标圆心。 */
	private readonly projectedCenterWorld: THREE.Vector3;
	/** 投影圆的固定半径。 */
	private readonly projectedRadius: number;
	/** 关系面板显示的来源边标签。 */
	private readonly edgeLabel: string;

	/** @param options 外部圆投影参数。 */
	public constructor(options: ReferenceDrivenCircleCoincidentConstraintOptions) {
		super();
		this.referenceId = options.referenceId;
		this.projectedCenterWorld = options.projectedCenterWorld.clone();
		this.projectedRadius = options.projectedRadius;
		this.edgeLabel = resolvePreferredEdgeLabel({
			runtimeSourceName: options.runtimeSourceName,
			persistedSourceDisplayLabel: options.sourceDisplayLabel,
		});
		this.name = this.edgeLabel;
	}

	/** @returns 关系面板使用的稳定显示名。 */
	public override get baseDisplayName(): string {
		return this.edgeLabel;
	}

	/** @returns 与当前投影载荷相互独立的新关系实例。 */
	public override cloneSelf(): ReferenceDrivenCircleCoincidentConstraint {
		return new ReferenceDrivenCircleCoincidentConstraint({
			referenceId: this.referenceId,
			projectedCenterWorld: this.projectedCenterWorld.clone(),
			projectedRadius: this.projectedRadius,
		});
	}

	/**
	 * 创建统一参数方程使用的局部固定圆载荷。
	 *
	 * @returns 目标草图参考面局部坐标中的外部圆；目标尚未绑定时返回 `null`。
	 */
	public override createParametricFormulaPayload() {
		const target = this.getTargetOrThrow();
		if (!target) {
			return null;
		}

		// 世界坐标只在引用边界存在，进入求解前统一投影为草图局部参数。
		const center = target.referencePlane.worldToLocal(this.projectedCenterWorld);
		return {
			kind: 'external-circle',
			referenceId: this.referenceId,
			center: [center.x, center.y],
			radius: this.projectedRadius,
		};
	}
}
